import { Fragment, useState } from 'react'

import { createDataAttribute } from 'next-sanity'

import { NavigationItem } from '@/app/components/NavigationItem'
import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { NavQueryResult } from '@/sanity.types'

interface NavigationProps {
  classNames?: {
    root?: string
    label?: string
    list?: string
    item?: string
    itemDescription?: string
    itemOpened?: string
    button?: string
    link?: string
    separator?: string
    collapseButton?: string
    collapseIndicator?: string
    subList?: string
    subListOpened?: string
    subItem?: string
    subItemDescription?: string
    subButton?: string
    subLink?: string
  }
  collapsible?: boolean
  collapseMode?: 'independent' | 'accordion'
  defaultOpen?: boolean
  ignoreButtonStyles?: boolean
  includeItemDescription?: boolean
  includeSeparator?: boolean
  label?: string
  nav: NavQueryResult[number] | null
  themeMode?: 'light' | 'dark'
  tracking: TrackingLocation
  variantMapping?: Record<string, string>
}

export const Navigation = function ({
  classNames,
  collapsible = false,
  collapseMode = 'independent',
  defaultOpen = false,
  ignoreButtonStyles = false,
  includeItemDescription,
  includeSeparator,
  label,
  nav,
  themeMode,
  tracking,
  variantMapping,
}: NavigationProps) {
  const [openItemKey, setOpenItemKey] = useState<string | null>(
    defaultOpen && nav?.menu.items?.[0]?._key ? nav.menu.items[0]._key : null,
  )

  if (!nav) {
    return null
  }

  const attr = createDataAttribute({
    id: nav._id,
    type: 'navigation',
    path: 'navigation',
  })

  const handleItemToggle = (itemKey: string, isOpen: boolean) => {
    if (collapseMode === 'accordion') {
      setOpenItemKey(isOpen ? itemKey : null)
    }
  }

  return (
    <nav className={cn(classNames?.root)} data-sanity={attr.toString()}>
      {label && (
        <p className={cn('size-header-h6 text-paved-white font-book', classNames?.label)}>
          {label}
        </p>
      )}
      <ul role="list" className={cn('', classNames?.list)}>
        {nav.menu.items?.map((item, index) => (
          <Fragment key={item._key}>
            <NavigationItem
              classNames={{
                root: classNames?.item,
                button: classNames?.button,
                description: classNames?.itemDescription,
                link: classNames?.link,
                collapseButton: classNames?.collapseButton,
                collapseIndicator: classNames?.collapseIndicator,
                itemOpened: classNames?.itemOpened,
              }}
              subItemClassNames={{
                list: classNames?.subList,
                listOpened: classNames?.subListOpened,
                root: classNames?.subItem,
                button: classNames?.subButton,
                description: classNames?.subItemDescription,
                link: classNames?.subLink,
              }}
              collapsible={collapsible}
              {...(collapseMode === 'accordion'
                ? {
                    isOpen: openItemKey === item._key,
                    onToggle: (isOpen: boolean) => handleItemToggle(item._key, isOpen),
                  }
                : { defaultOpen })}
              ignoreButtonStyles={ignoreButtonStyles}
              includeDescription={includeItemDescription}
              item={item}
              themeMode={themeMode}
              tracking={tracking}
              variantMapping={variantMapping}
            />
            {includeSeparator && index < (nav.menu.items?.length ?? 0) - 1 && (
              <li className={classNames?.separator} />
            )}
          </Fragment>
        ))}
      </ul>
    </nav>
  )
}
